/**
 * Sentry bridge — initializes @sentry/browser when a DSN is provided and
 * forwards Elm-side errors via the `reportError` port.
 *
 * No-op (just logs to console) when VITE_SENTRY_DSN is unset, so local dev
 * doesn't accidentally pollute the production project.
 */

import * as Sentry from "@sentry/browser";

type ErrorReport = {
  readonly tag: string;
  readonly message: string;
  readonly context: unknown;
};

type Ports = {
  readonly reportError?: { readonly subscribe: (h: (r: ErrorReport) => void) => void } | undefined;
};

type Config = {
  readonly dsn: string | undefined;
  readonly environment: string;
};

const sentryEnabled = (config: Config): config is { dsn: string; environment: string } =>
  typeof config.dsn === "string" && config.dsn.length > 0;

export const wireSentryBridge = (config: Config, ports: Ports): void => {
  // Elm dead-code-eliminates unused ports, so reportError may be undefined
  // until something in the Elm app actually calls it. Skip silently.
  if (ports.reportError === undefined) return;

  if (!sentryEnabled(config)) {
    ports.reportError.subscribe((report) => {
      console.error(`[sentry:disabled] ${report.tag}`, report.message, report.context);
    });
    return;
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    tracesSampleRate: 0,
    sendDefaultPii: false,
  });

  ports.reportError.subscribe((report) => {
    Sentry.captureMessage(report.message, {
      level: "error",
      tags: { tag: report.tag, env: config.environment },
      extra: { context: report.context },
    });
  });
};
